// Shared markdown typography. Compose with an sx array when a call site needs
// small overrides, e.g. sx={[MARKDOWN_PROSE_SX, { "& img": { display: "none" } }]}.
import {
  ACCENT,
  BADGE_COLORS,
  FONT,
  HAIRLINE,
  HAIRLINE_STRONG,
  INK,
  INK_SECONDARY,
  RADIUS,
  STATUS,
  SURFACE_MUTED,
  withAlpha,
} from "../theme/tokens";

// GitHub alert levels mapped onto app colors.
const ALERT_COLORS = {
  note: STATUS.info,
  tip: STATUS.success,
  important: BADGE_COLORS[1], // violet
  warning: STATUS.warning,
  caution: STATUS.error,
};

export const MARKDOWN_MONO_FONT =
  '"JetBrains Mono", "Fira Mono", "Cascadia Code", "Menlo", "Consolas", monospace';

export const MARKDOWN_PROSE_SX = {
  color: INK,
  fontSize: FONT.md,
  lineHeight: 1.7,
  overflowWrap: "anywhere",
  "& > :first-of-type": { mt: 0 },
  "& > :last-child": { mb: 0 },
  "& h1, & h2, & h3": {
    fontWeight: 600,
    color: INK,
    mt: 2,
    mb: 1,
    lineHeight: 1.3,
  },
  "& h1": { fontSize: FONT.xxl },
  "& h2": { fontSize: FONT.xl },
  "& h3": { fontSize: FONT.lg },
  "& p": { my: 1 },
  "& ul, & ol": { pl: 3, my: 1 },
  "& li": { my: 0.5 },
  "& code": {
    fontFamily: MARKDOWN_MONO_FONT,
    fontSize: FONT.sm,
    backgroundColor: SURFACE_MUTED,
    px: 0.5,
    py: "1px",
    borderRadius: `${RADIUS.sm}px`,
  },
  // Fenced code blocks: one scrollable panel, no double-padding.
  "& pre": {
    backgroundColor: SURFACE_MUTED,
    border: `1px solid ${HAIRLINE}`,
    borderRadius: `${RADIUS.sm}px`,
    p: 1.5,
    my: 1.5,
    overflow: "auto",
  },
  "& pre code": {
    backgroundColor: "transparent",
    p: 0,
    fontSize: FONT.sm,
  },
  "& a": {
    color: ACCENT,
    textDecoration: "none",
    "&:hover": { textDecoration: "underline" },
  },
  "& blockquote": {
    borderLeft: `3px solid ${HAIRLINE_STRONG}`,
    pl: 2,
    ml: 0,
    my: 1,
    color: INK_SECONDARY,
  },
  // GFM tables.
  "& table": {
    borderCollapse: "collapse",
    width: "auto",
    my: 1.5,
    fontSize: FONT.sm,
  },
  "& th, & td": {
    border: `1px solid ${HAIRLINE}`,
    px: 1.25,
    py: 0.75,
    textAlign: "left",
  },
  "& th": { backgroundColor: SURFACE_MUTED, fontWeight: 600 },
  "& hr": {
    border: 0,
    borderTop: `1px solid ${HAIRLINE}`,
    my: 2,
  },
  "& img": { maxWidth: "100%" },
  // GFM task-list checkboxes render as <input>; keep them inline.
  "& input[type=checkbox]": { mr: 0.75 },

  // GitHub-style alert callouts from markdownAlerts.js.
  "& .markdown-alert": {
    borderLeft: "4px solid",
    borderRadius: `${RADIUS.sm}px`,
    px: 2,
    py: 1.25,
    my: 1.5,
  },
  "& .markdown-alert p": { my: 0.5 },
  "& .markdown-alert-title": {
    fontWeight: 700,
    fontSize: FONT.sm,
    mt: 0,
  },
  ...Object.fromEntries(
    Object.entries(ALERT_COLORS).flatMap(([className, color]) => [
      [
        `& .markdown-alert-${className}`,
        { borderLeftColor: color, backgroundColor: withAlpha(color, 0.08) },
      ],
      [`& .markdown-alert-${className} .markdown-alert-title`, { color }],
    ]),
  ),
};
